import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Close, ChevronLeft, ChevronRight, KeyboardArrowDown } from '@mui/icons-material';
import './HomeGallery.css';

// Optimized Lazy Image Component with Intersection Observer
const LazyImage = React.memo<{ 
  src: string; 
  alt: string; 
  className?: string;
  onClick?: () => void;
}>(({ src, alt, className, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = imgRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { 
        rootMargin: '100px', // Start loading earlier
        threshold: 0.01 
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div 
      ref={imgRef} 
      className={`relative ${className || ''}`} 
      onClick={onClick}
      style={{ willChange: 'opacity' }}
    >
      {isInView ? (
        <>
          {!isLoaded && (
            <div className="absolute inset-0 bg-gray-800 rounded-lg" />
          )}
          <img
            src={src}
            alt={alt}
            className={`w-full h-full object-cover ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setIsLoaded(true)}
            loading="lazy"
            decoding="async"
            style={{ 
              transition: 'opacity 0.2s ease-in',
              willChange: 'opacity'
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-800 rounded-lg" />
      )}
    </div>
  );
});

const HomeGallery: React.FC = () => {
  // Sample of images from the gallery - you can add more
  const images = [
    '/images/_B4A8061.JPG',
    '/images/_B4A8071.JPG',
    '/images/_B4A8074.JPG',
    '/images/_B4A8514.JPG',
    '/images/_B4A8559.JPG',
    '/images/_B4A8704.JPG',
    '/images/1A2A1273.JPG',
    '/images/1A2A1280.JPG',
    '/images/1A2A1299.JPG',
    '/images/1A2A1424.JPG',
    '/images/A__12.JPG',
    '/images/A__29.JPG',
    '/images/A__55.JPG',
    '/images/Bizani24_Hunyani_b__53.JPG',
    '/images/BYO__10.JPG',
    '/images/C__13.JPG',
  ];

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  // Show only 2 rows initially (8 images for 4 columns, 6 for 3 columns, 4 for 2 columns)
  const imagesPerRow = 4; // Based on lg:grid-cols-4
  const initialImageCount = imagesPerRow * 2; // 2 rows
  const displayedImages = showAll ? images : images.slice(0, initialImageCount);

  // Preload only first 2 images for instant display (reduced from 4)
  useEffect(() => {
    const preloadImages = displayedImages.slice(0, 2);
    const links: HTMLLinkElement[] = [];
    
    preloadImages.forEach((src) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      links.push(link);
    });

    return () => {
      links.forEach((link) => link.remove());
    };
  }, [displayedImages]);

  const openModal = (index: number) => {
    setSelectedIndex(index);
  };

  const closeModal = () => {
    setSelectedIndex(null);
  };

  const goToPrevious = React.useCallback(() => {
    if (selectedIndex !== null && displayedImages.length > 0) {
      setSelectedIndex(selectedIndex === 0 ? displayedImages.length - 1 : selectedIndex - 1);
    }
  }, [selectedIndex, displayedImages]);

  const goToNext = React.useCallback(() => {
    if (selectedIndex !== null && displayedImages.length > 0) {
      setSelectedIndex(selectedIndex === displayedImages.length - 1 ? 0 : selectedIndex + 1);
    }
  }, [selectedIndex, displayedImages]);

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, goToPrevious, goToNext]);

  // Update image size when image loads or changes
  useEffect(() => {
    const updateImageSize = () => {
      if (imageRef.current) {
        setImageSize({
          width: imageRef.current.offsetWidth,
          height: imageRef.current.offsetHeight
        });
      }
    };

    // Capture the current ref value
    const currentImageRef = imageRef.current;

    if (selectedIndex !== null && currentImageRef) {
      if (currentImageRef.complete) {
        updateImageSize();
      } else {
        currentImageRef.addEventListener('load', updateImageSize);
      }
      
      // Also update on window resize
      window.addEventListener('resize', updateImageSize);
      
      return () => {
        if (currentImageRef) {
          currentImageRef.removeEventListener('load', updateImageSize);
        }
        window.removeEventListener('resize', updateImageSize);
      };
    }
  }, [selectedIndex]);

  return (
    <section className="py-20 bg-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ 
              fontFamily: "'Montserrat', sans-serif",
              color: '#EF3F23'
            }}
          >
            Inside Our Studio
          </h2>
          <p
            className="text-lg md:text-xl text-white max-w-2xl mx-auto"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            A Glimpse Inside Our Creative Space.
          </p>
        </motion.div>

        {/* Image Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 home-gallery-grid">
            {displayedImages.map((image, displayIndex) => (
              <motion.div
                key={`gallery-${displayIndex}-${image}`}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="relative overflow-hidden rounded-lg cursor-pointer group"
                style={{ 
                  aspectRatio: '1/1',
                  willChange: 'transform'
                }}
                onClick={() => openModal(displayIndex)}
              >
                <LazyImage
                  src={image}
                  alt={`Gallery image ${displayIndex + 1}`}
                  className="w-full h-full"
                />
                <div 
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/20 pointer-events-none"
                  style={{ transition: 'background-color 0.15s ease' }}
                ></div>
              </motion.div>
            ))}
          </div>

          {/* View More Icon */}
          {!showAll && images.length > initialImageCount && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center mt-8"
            >
              <motion.div
                onClick={() => setShowAll(true)}
                className="cursor-pointer"
                animate={{
                  y: [0, -10, 0]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                whileHover={{
                  scale: 1.2
                }}
              >
                <KeyboardArrowDown 
                  style={{ 
                    fontSize: '48px',
                    color: '#EF3F23'
                  }} 
                />
              </motion.div>
            </motion.div>
          )}
        </motion.div>

        {/* Full Screen Modal */}
        <AnimatePresence>
          {selectedIndex !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={closeModal}
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-60 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-200 backdrop-blur-sm"
                aria-label="Close"
              >
                <Close style={{ fontSize: '28px' }} />
              </button>

              {/* Navigation Arrows */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-60 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-200 backdrop-blur-sm"
                aria-label="Previous image"
              >
                <ChevronLeft style={{ fontSize: '28px' }} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-60 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-200 backdrop-blur-sm"
                aria-label="Next image"
              >
                <ChevronRight style={{ fontSize: '28px' }} />
              </button>

              {/* Image Container */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative inline-block">
                  {selectedIndex !== null && displayedImages[selectedIndex] && (
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={selectedIndex}
                        ref={imageRef}
                        src={displayedImages[selectedIndex]}
                        alt={`Gallery image ${selectedIndex + 1}`}
                        className="max-w-full max-h-[90vh] object-contain block"
                        loading="eager"
                        decoding="sync"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onLoad={() => {
                          if (imageRef.current) {
                            setImageSize({
                              width: imageRef.current.offsetWidth,
                              height: imageRef.current.offsetHeight
                            });
                          }
                        }}
                      />
                    </AnimatePresence>
                  )}
                  
                  {/* Tagline on Image - positioned relative to actual image */}
                  {imageSize.width > 0 && (
                    <div
                      className="absolute"
                      style={{ 
                        bottom: '16px',
                        right: '16px',
                        zIndex: 10
                      }}
                    >
                      <p
                        className="text-sm sm:text-base md:text-lg lg:text-xl"
                        style={{ 
                          fontFamily: "'HolidayFree', sans-serif",
                          color: '#EF3F23',
                          opacity: 0.7
                        }}
                      >
                        documenting real life
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Image Counter */}
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-60 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm text-white text-sm"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {selectedIndex + 1} / {displayedImages.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HomeGallery;
